import { ApiError, api } from '../../lib/api'
import { INVITATION_STATUS } from '../../domain/domainCodes'
export { INVITATION_STATUS } from '../../domain/domainCodes'
import { dataParamsForm, readDataTable } from '../../lib/dataTable'
import { toFormData } from '../../lib/form'
import { upload } from '../../lib/upload'
import { isEnglish } from '../../lib/i18n'

/**
 * Invitations — `Features/Admin/Invitations/Endpoint.cs`.
 *
 *   getInvitationsPage -> GET  /api/admin/invitations/
 *   listInvitations    -> POST /api/admin/invitations/filter        form + DataTables keys
 *   saveInvitation     -> POST /api/admin/invitations/save          form
 *   saveBulk           -> POST /api/admin/invitations/save-bulk     multipart: excelFile
 *   resendEmail        -> POST /api/admin/invitations/resend-email  form: id
 *   changeStatus       -> POST /api/admin/invitations/change-status form: Id, Status
 *
 * EVERY ACTION IS FORM-ENCODED. The handlers take `HttpRequest` and call
 * `ReadFormAsync()` themselves — there is no model binding — so a JSON body reaches them
 * as an empty form and each field falls back to its default: `Guid.Empty` for the id,
 * `0` for the status. Nothing errors; the wrong invitation is written.
 *
 * The field names are read literally and their CASING IS NOT CONSISTENT between actions:
 * `resend-email` reads `id`, everything else reads `Id`.
 */

const label = (arabic, english) => (isEnglish ? english : arabic) || arabic || english || ''

/** `2026-08-08T14:05:22` -> `2026-08-08 14:05:22`, the way the table shows it. */
const asCell = (value) => (value ? String(value).slice(0, 19).replace('T', ' ') : '')

/** dbo.Invitations.Status — 0 new, 1 accepted, 2 cancelled. */

/** The departments the invite form offers. */
export async function getInvitationsPage(signal) {
  const dto = (await api.get('/api/admin/invitations/', { signal })) ?? {}

  return {
    /* The VALUE is the English name, not the id: `SaveInvitationCommand.GroupName` is a
       string and dbo.Invitations stores exactly that ("COEIA", "Intern 2025"). */
    groups: (dto.Groups ?? []).map((group) => ({
      value: group.EnglishName,
      label: label(group.ArabicName, group.EnglishName),
    })),
  }
}

/**
 * @param {object} state
 * @param {number|null} [state.status]  0 | 1 | 2, or null for every status
 * @param {boolean|null} [state.isAdmin]
 */
export async function listInvitations({
  status = null,
  isAdmin = null,
  page = 1,
  pageSize = 10,
  search = '',
  sort,
  signal,
} = {}) {
  /* `Status` and `IsAdmin` ride alongside the DataTables keys in the same form. Both are
     parsed with `TryParse`, so an empty string means "no filter" — which is exactly what
     omitting them does, and why they are dropped rather than sent blank. */
  const extra = {}
  if (status !== null && status !== '') extra.Status = String(status)
  if (isAdmin !== null && isAdmin !== '') extra.IsAdmin = String(isAdmin)

  const form = dataParamsForm(
    { page, pageSize, search, sort, columns: ['email', 'groupName', 'status', 'createdOn'] },
    extra,
  )

  const response = await api.post('/api/admin/invitations/filter', form, { signal })
  const { rows, total, totalUnfiltered } = readDataTable(response)

  return {
    rows: rows.map((row) => ({
      id: row.Id,
      email: row.Email ?? '',
      groupName: row.GroupName ?? '',
      status: row.Status ?? INVITATION_STATUS.SENT,
      isAdmin: row.IsAdmin ?? false,
      createdOnText: asCell(row.CreatedOn),
      expiryDateText: asCell(row.ExpiryDate),
      // Kept raw as well: the edit form seeds its date field from this.
      expiryDate: row.ExpiryDate ?? '',
      lastUpdateText: asCell(row.LastUpdate),
    })),
    total,
    totalUnfiltered,
  }
}

/** `{ ok, message, id }` — one shape for all four actions. */
async function act(path, body) {
  try {
    const data = await api.post(`/api/admin/invitations/${path}`, body)
    return { ok: data?.Result !== false, message: data?.Message ?? '', id: data?.Id }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

/**
 * THE EXPIRY ONLY ARRIVES IN ONE FORMAT, and it is not the obvious one.
 *
 * `Endpoint.cs` reads it with `DateTime.TryParse(form["ExpiryDate"], …)`, which parses in
 * the REQUEST's culture — ar-SA, whose calendar is Umm al-Qura. A Gregorian `2026-09-05`
 * or `2026-09-05 23:59:59` is not a date in that calendar, so `TryParse` fails, the value
 * is dropped without a word and the handler falls back to "thirty hours from now".
 *
 * Verified against the running API: of `2026-09-05`, `2026-09-05 23:59:59`, `09/05/2026`,
 * `2026-09-05T23:59:59` and `…T23:59:59Z`, only the two with a `T` were stored, and the
 * `Z` one arrived three hours late. So: `T`, no zone. API_FINDINGS.md §-.
 *
 * The chosen DAY is honoured to its END. An expiry is a date on this screen — "valid until
 * the 5th" — and midnight at the START of the 5th would make a link picked for today
 * expire before it could be sent.
 */
const asExpiry = (value) => {
  if (!value) return ''
  const day = String(value).slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T23:59:59` : ''
}

/**
 * Create and update take the same form; an empty `Id` parses to `Guid.Empty`, which is
 * what the handler reads as "new".
 */
export function saveInvitation(values) {
  return act(
    'save',
    toFormData({
      Id: values.id ?? '',
      Email: values.email ?? '',
      GroupName: values.groupName ?? '',
      Status: String(values.status ?? INVITATION_STATUS.SENT),
      IsAdmin: String(values.isAdmin ?? false),
      ExpiryDate: asExpiry(values.expiryDate),
    }),
  )
}

/** Lower-case `id` here, unlike every other action. Not a typo — see the note above. */
export const resendInvitationEmail = (id) => act('resend-email', toFormData({ id }))

export const changeInvitationStatus = (id, status) =>
  act('change-status', toFormData({ Id: id, Status: String(status) }))

/**
 * A spreadsheet of email addresses, one invitation each.
 *
 * The reply counts what it took (`SuccessCount`), which is worth showing: a file of 200
 * addresses that produced 40 invitations is not a clean success. An omitted expiry
 * becomes "30 hours from now" server-side.
 */
export async function saveBulkInvitations({ file, expiryDate }) {
  if (!file) return { ok: false, message: '', successCount: 0 }

  const form = new FormData()
  form.append('excelFile', file)
  const expiry = asExpiry(expiryDate)
  if (expiry) form.append('ExpiryDate', expiry)

  try {
    const data = await upload('/api/admin/invitations/save-bulk', form)
    return {
      ok: data?.Result !== false,
      message: data?.Message ?? '',
      successCount: data?.SuccessCount ?? 0,
    }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message, successCount: 0 }
    throw error
  }
}

/**
 * The four figures above the table.
 *
 * The list is server-paged, so the rows on screen are one page and cannot be counted for a
 * summary. `RecordsTotal` is the count AFTER the status filter and BEFORE the search
 * (`FilterInvitationsHandler:32`), so asking for one row of each status gives the server's
 * own figures — four tiny requests rather than a number derived from a page.
 */
export async function countInvitations(signal) {
  const one = async (status) => {
    const response = await listInvitations({ status, page: 1, pageSize: 1, signal })
    return response.totalUnfiltered
  }

  const [total, newCount, accepted, cancelled] = await Promise.all([
    one(null),
    one(INVITATION_STATUS.SENT),
    one(INVITATION_STATUS.USER_REGISTERED),
    one(INVITATION_STATUS.CANCELED),
  ])

  return { total, 0: newCount, 1: accepted, 2: cancelled }
}
